/**
 * types.ts — TypeScript mirrors of the Helm wire & API contract (SPEC.md).
 *
 * EVERY shape here must match SPEC.md exactly. Do not invent fields, rename
 * channels, or change message `type`/`kind` discriminants. If the SPEC changes,
 * change it there first, then here.
 *
 * Sources:
 *   §2  Auth / JWT claims
 *   §3  REST resources
 *   §4  Session establishment + signaling envelope
 *   §9  Data channels, input events (§9.1), file framing (§9.2)
 *   §10 Per-session E2EE
 */

// ---------------------------------------------------------------------------
// §1 Identity / roles
// ---------------------------------------------------------------------------
export type Role = 'super_admin' | 'org_admin' | 'technician' | 'read_only' | 'client';

export type SessionMode = 'view' | 'control';
export type SessionStatus = 'pending' | 'active' | 'ended' | 'failed';

// ---------------------------------------------------------------------------
// §2 Auth tokens & JWT claims
// ---------------------------------------------------------------------------
export interface AccessClaims {
  sub: string;        // user_id
  org_id: string;
  role: Role;
  scope: 'console';
  iat: number;
  exp: number;
  jti: string;
}

/** Token pair returned by login/webauthn-finish/refresh. */
export interface TokenPair {
  access: string;
  refresh: string;
}

/**
 * GET /api/v1/auth/config — public, unauthenticated. Tells the console which
 * sign-in methods to surface (server handlers_oidc.go handleAuthConfig).
 *   - oidc_enabled: show the "Sign in with Microsoft" button.
 *   - oidc_only: hide the password/org/email form entirely (Entra-only tenant).
 */
export interface AuthConfig {
  oidc_enabled: boolean;
  oidc_only: boolean;
}

/** POST /auth/login response: either tokens, or a WebAuthn step-up challenge. */
export interface LoginPasswordResult {
  webauthn_required?: true;
  challenge_id?: string;
  // The assertion options for the password-verified challenge, returned inline so
  // the console completes the assertion against THIS challenge (true 2FA) instead
  // of starting a fresh, password-less one (codex NEW HIGH). Same shape as
  // WebAuthnLoginBegin.public_key.
  public_key?: { publicKey: PublicKeyCredentialRequestOptionsJSON };
  // First-run enrollment window (no passkey yet): the server returns a
  // short-lived, registration-ONLY token instead of console tokens (codex High
  // #5). The console must immediately run passkey registration with this token;
  // it grants no console access. Full tokens come back from register/finish.
  passkey_registration_required?: true;
  register_token?: string;
  // Legacy/back-compat: older servers returned console tokens directly on first
  // run — see SPEC §2.
  access?: string;
  refresh?: string;
}

// POST /auth/webauthn/register/finish. On a first-run (registration-token)
// ceremony the server upgrades the account and returns full console tokens
// alongside the status (codex High #5); when an already-logged-in user adds
// another passkey, only the status fields are present.
export interface WebAuthnRegisterFinish {
  status: string;
  credential_id: string;
  access?: string;
  refresh?: string;
}

// WebAuthn options are the standard PublicKey*Options serialized with base64url
// for the binary members. We keep them as the JSON wire form and convert in
// src/auth using the browser's native (de)serialization helpers.
// The server returns the go-webauthn CredentialAssertion/CredentialCreation JSON
// under the snake_case key `public_key` (handlers_webauthn.go). That object is
// itself `{ publicKey: <options> }` (the WebAuthn wire shape), so the actual
// options live at `public_key.publicKey`.
export interface WebAuthnLoginBegin {
  challenge_id: string;
  public_key: { publicKey: PublicKeyCredentialRequestOptionsJSON };
}

export interface WebAuthnRegisterBegin {
  challenge_id: string;
  public_key: { publicKey: PublicKeyCredentialCreationOptionsJSON };
}

// Minimal JSON shapes (subset of the WebAuthn L3 *JSON types) so we don't need
// a dependency. Binary members are base64url strings on the wire.
export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string; // base64url
  timeout?: number;
  rpId?: string;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: PublicKeyCredentialDescriptorJSON[];
}

export interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string; // base64url
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string }; // user.id base64url
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  excludeCredentials?: PublicKeyCredentialDescriptorJSON[];
  authenticatorSelection?: {
    authenticatorAttachment?: AuthenticatorAttachment;
    residentKey?: ResidentKeyRequirement;
    requireResidentKey?: boolean;
    userVerification?: UserVerificationRequirement;
  };
}

export interface PublicKeyCredentialDescriptorJSON {
  type: 'public-key';
  id: string; // base64url
  transports?: AuthenticatorTransport[];
}

// ---------------------------------------------------------------------------
// §3 REST DTOs
// ---------------------------------------------------------------------------
export interface Org {
  id: string;
  parent_org_id: string | null;
  name: string;
  slug: string;
  branding: Branding;
  policy: Record<string, unknown>;
  bootstrap_done: boolean;
  created_at: string;
}

export interface Branding {
  name?: string;
  logo_url?: string;
  color?: string;
}

export interface User {
  id: string;
  org_id: string;
  email: string;
  display_name: string;
  role: Role;
  status: string;
  created_at: string;
}

export interface Device {
  id: string;
  org_id: string;
  name: string;
  hostname: string;
  os: string;
  os_version: string;
  ephemeral: boolean;
  online: boolean;            // §3: GET /devices includes `online`
  last_seen_at: string | null;
  agent_version: string;      // running agent build (reported on WS hello)
  // §3: includes `groups`. The server returns group NAMES (deviceView.groups is
  // []string from GroupsForDevice), so this mirrors that exactly.
  groups: string[];
  last_sysinfo?: SysInfoSnapshot | null;
  // C1 (SPEC §10.1): the agent's Noise X25519 static public key (hex, 32 bytes)
  // and its CA-root attestation (hex, RAW P1363 ECDSA r||s, 64 bytes) over
  // (device_id, noise_pubkey). The console cert-binds the Noise key against an
  // out-of-band-pinned CA root before trusting it (see lib/e2ee verifyNoiseAttestation).
  noise_pubkey?: string;
  noise_attestation?: string;
}

/** GET /api/v1/ca/root-pubkey (SPEC §10.1): served CA root for attestation. */
export interface CARootPubKey {
  root_pubkey: string; // hex DER SubjectPublicKeyInfo, ECDSA P-256
}

/** GET /api/v1/agent-release: the agent build the deployment publishes as latest. */
export interface AgentRelease {
  version: string; // published target (HELM_AGENT_TARGET_VERSION); '' when unset
  available: boolean; // target set AND a binary is present to serve
  auto_update: boolean; // fleet auto-update master switch (informational)
}

/** POST /api/v1/devices/{id}/agent-update result. */
export interface AgentUpdateResult {
  status: 'pushed' | 'already_current';
  version: string;
}

/** POST /api/v1/devices/agent-update (fleet) result. */
export interface AgentUpdateBulkResult {
  status: 'ok';
  pushed: number;
  offline_skipped: number;
  version: string;
}

export interface DeviceGroup {
  id: string;
  org_id: string;
  name: string;
}

export interface SysInfoSnapshot {
  hostname?: string;
  os?: string;
  os_version?: string;
  cpu?: string;
  mem_total?: number;
  mem_used?: number;
  uptime_sec?: number;
  [k: string]: unknown;
}

export interface Session {
  id: string;
  org_id: string;
  device_id: string;
  user_id: string | null;
  mode: SessionMode;
  status: SessionStatus;
  consent_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  bytes_relayed: number;
  created_at: string;
}

export interface SessionEvent {
  id: number;
  org_id: string;
  session_id: string;
  kind: string;
  data: Record<string, unknown>;
  ts: string;
}

export interface SessionDetail extends Session {
  events: SessionEvent[];
}

/** Ephemeral coturn creds (SPEC §4 / §5). */
export interface TurnCredentials {
  urls: string[];
  username: string;   // "<unix_expiry>:<session_id>"
  credential: string; // base64(HMAC-SHA1(secret, username))
}

/** POST /sessions response (SPEC §4 step 1). */
export interface SessionStartResult {
  session_id: string;
  signal_url: string;     // wss://HOST/api/v1/session/<id>/signal
  signal_token: string;   // short-lived session JWT
  turn: TurnCredentials;
  mode: SessionMode;
}

/**
 * POST /sessions/launch-token response — the one-time token + the parameters the
 * console needs to build the helm:// deep link that launches the native client.
 * server_url + org_slug come from the server (the console doesn't persist its slug).
 */
export interface LaunchToken {
  token: string;
  server_url: string;
  org_slug: string;
  device_id: string;
  mode: SessionMode;
  expires_at: string;
}

/** POST /support-codes (SPEC §6). */
export interface SupportCode {
  code: string;
  expires_at: string;
}

/** POST /installers (SPEC §7). */
export interface InstallerResult {
  installer_token: string;
  download_url: string;
  config: InstallerConfig;
}

export interface InstallerConfig {
  server_url: string;
  org_id: string;
  enrollment_token: string;
  branding: Branding;
}

/** Audit event row (SPEC §8). Hashes are base64 on the wire. */
export interface AuditEvent {
  id: number;
  org_id: string;
  prev_hash: string; // base64
  hash: string;      // base64
  actor: string;     // user:<id> | device:<id> | system
  action: string;
  target: string;
  data: Record<string, unknown>;
  ts: string;
}

// GET /recordings/{id} (server handlers_admin.go). The signed playback_url is
// produced by the storage layer at deploy time (empty on the build/dev machine —
// TODO(hardware): object store + per-tenant KEK signing).
export interface Recording {
  id: string;
  object_key: string;
  size_bytes: number;
  enc_key_id: string;
  sha256: string;        // hex
  playback_url: string;  // signed by the storage layer; empty until provisioned
}

/** Audit hash-chain verification result returned alongside GET /audit (SPEC §8). */
export interface AuditVerify {
  ok: boolean;
  count: number;
  broken_at_id?: number;
  reason?: string;
}

// ---------------------------------------------------------------------------
// §4 Signaling WS envelope (browser ↔ broker ↔ agent)
// ---------------------------------------------------------------------------
export type SignalType =
  | 'hello'
  | 'session_request'
  | 'sdp_offer'
  | 'sdp_answer'
  | 'ice_candidate'
  | 'session_end'
  | 'heartbeat'
  | 'error';

export interface SignalEnvelope<P = unknown> {
  type: SignalType;
  session_id: string;
  payload: P;
  ts: number;
}

export interface SdpPayload {
  sdp: string;
  type: 'offer' | 'answer';
}

export interface IceCandidatePayload {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
}

export interface ErrorPayload {
  code: string;
  message: string;
}

// ---------------------------------------------------------------------------
// §9 Data channels
// ---------------------------------------------------------------------------
/** Channel labels are fixed by SPEC §9 — do not rename. */
export const CHANNELS = {
  control: 'control',
  input: 'input',
  clipboard: 'clipboard',
  shell: 'shell',
  files: 'files',
  sysinfo: 'sysinfo',
} as const;
export type ChannelLabel = (typeof CHANNELS)[keyof typeof CHANNELS];

// ----- control channel (JSON): E2EE handshake, monitor list, cursor, control
//
// WIRE SHAPE (must match agent internal/proto.ControlMsg exactly):
//   { "kind": "...", "b64"?: "<base64 Noise bytes>", "data"?: <structured> }
// Handshake frames carry their bytes in `b64`; structured control frames carry a
// typed payload in `data`. The kind discriminants are the agent's constants:
//   e2ee_init | e2ee_resp | e2ee_final  (Noise_XK is a 3-message handshake)
//   monitors | set_monitor | cursor | keyframe | consent | session_end
export type ControlKind =
  | 'e2ee_init'
  | 'e2ee_resp'
  | 'e2ee_final'
  | 'monitors'
  | 'set_monitor'
  | 'cursor'
  | 'keyframe'
  | 'consent'
  | 'session_end';

/** A single display, as enumerated by the agent (proto.Monitor). */
export interface MonitorInfo {
  id: number;
  name: string;
  width: number;
  height: number;
  x: number;
  y: number;
  primary: boolean;
}

/** `monitors` payload (proto.MonitorList). */
export interface MonitorList {
  monitors: MonitorInfo[];
  active: number;
}

/** `set_monitor` payload (proto.SetMonitor). */
export interface SetMonitorData {
  monitor: number;
}

/** `cursor` payload (proto.Cursor). */
export interface CursorData {
  x: number;
  y: number;
  monitor: number;
  visible: boolean;
  image_b64?: string;
  hot_x?: number;
  hot_y?: number;
}

/** `consent` payload (agent → tech consent state). */
export interface ConsentData {
  granted: boolean;
  user?: string;
}

/**
 * The on-the-wire control envelope. `data` is the kind-specific structured
 * payload (JSON, decoded by the consumer); `b64` carries Noise handshake bytes.
 */
export interface ControlMessage {
  kind: ControlKind;
  b64?: string;
  data?: MonitorList | SetMonitorData | CursorData | ConsentData | Record<string, unknown>;
}

// ----- §9.1 input events (input channel, tech→agent)
export type InputEvent =
  | KeyInputEvent
  | MouseInputEvent
  | ScrollInputEvent
  | ViewOnlyInputEvent;

export interface KeyInputEvent {
  t: 'key';
  usage: number;     // HID usage code (primary)
  down: boolean;
  mods: number;      // bitmask 1=Shift 2=Ctrl 4=Alt 8=Meta
  unicode?: number;  // codepoint fallback
}

export interface MouseInputEvent {
  t: 'mouse';
  x: number;         // normalized 0..1
  y: number;         // normalized 0..1
  monitor: number;
  buttons: number;   // current button bitmask
  down: boolean;
  button: number;    // 0=left 1=right 2=middle
}

export interface ScrollInputEvent {
  t: 'scroll';
  dx: number;
  dy: number;
  monitor: number;
}

export interface ViewOnlyInputEvent {
  t: 'viewonly';
  on: boolean;
}

/** §9.1 modifier bitmask. */
export const MOD = { Shift: 1, Ctrl: 2, Alt: 4, Meta: 8 } as const;
/** §9.1 mouse button ids. */
export const MOUSE_BUTTON = { left: 0, right: 1, middle: 2 } as const;

// ----- clipboard channel (JSON)
export interface ClipboardMessage {
  format: 'text' | 'image';
  data: string; // base64
  hash: string;
}

// ----- shell channel (JSON)
export interface ShellMessage {
  kind: 'data' | 'resize' | 'open' | 'close';
  data?: string;
  cols?: number;
  rows?: number;
  shell?: string;
}

// ----- sysinfo channel (JSON)
export interface SysInfoMessage {
  kind: 'request' | 'response';
  data?: SysInfoSnapshot;
}

// ----- §9.2 file-transfer framing headers (binary framed on `files`)
export type FileHeader =
  | { kind: 'offer'; transfer_id: string; name: string; size: number; sha256: string }
  | { kind: 'accept'; transfer_id: string }
  | { kind: 'chunk'; transfer_id: string; seq: number; offset: number }
  | { kind: 'ack'; transfer_id: string; seq: number }
  | { kind: 'complete'; transfer_id: string }
  | { kind: 'error'; transfer_id: string; message: string };

// ---------------------------------------------------------------------------
// Connection-quality stats surfaced in the viewer.
// ---------------------------------------------------------------------------
export interface ConnStats {
  state: RTCPeerConnectionState;
  iceState: RTCIceConnectionState;
  candidatePair?: 'host' | 'srflx' | 'prflx' | 'relay' | 'unknown';
  rttMs?: number;
  bitrateKbps?: number;
  fps?: number;
  frameWidth?: number;
  frameHeight?: number;
  packetsLost?: number;
  jitterMs?: number;
}
